import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Box } from '@mui/material';
import { getAllInvoiceTemplate } from 'src/shared/services/invoiceService';
import { InvoiceTemplate } from 'src/modules/invoice/models/invoiceTemplate';
import { InvoiceTemplateOptions } from 'src/shared/models/options/invoiceTemplateOptions';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { AdminInvoiceTemplateDetail } from './adminInvoiceTemplateDetail';
import { AdminInvoiceTemplateSearchFilter } from '../models/adminInvoiceTemplateSearchFilter';

type AdminInvoiceTemplateDataGridProps = {
  searchFilter: AdminInvoiceTemplateSearchFilter;
  // En nyss skapad mall öppnas direkt för redigering. Modalen bor här, så sidan ber om det via id.
  templateIdToOpen?: number;
  onTemplateOpened?: () => void;
};
export const AdminInvoiceTemplateDataGrid = (props: AdminInvoiceTemplateDataGridProps) => {
  const { searchFilter, templateIdToOpen, onTemplateOpened } = props;
  const { t } = useTranslation(['invoice']);
  const isMounted = useIsMounted();

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'template_id', sort: 'desc' }]);
  const [openTemplateDetails, setOpenTemplateDetails] = useState<boolean>(false);
  const [templateId, setTemplateId] = useState<number>(0);

  const [rows, setRows] = useState<InvoiceTemplate[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('pageSize', newPageSize);
  };

  const onRowClick = (params) => {
    setTemplateId(params.row.template_id);
    setOpenTemplateDetails(true);
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }

    const opts = new InvoiceTemplateOptions({
      owner_id: searchFilter.ownerId || undefined,
      filter: searchFilter.searchString || undefined,
      pageSize: data.pageSize,
      pageIndex: data.page,
      sortCol: sortModel[0]?.field ?? 'owner_id',
      sortDir: (sortModel[0]?.sort ?? 'desc') as 'asc' | 'desc',
      // Visa medlemsavgift-familjen (member/autogiro/swish) samt gåvomallar (gift/gift_recurring).
      types: 'member,autogiro,swish,gift,gift_recurring',
    });

    opts.showFormInfo = true;

    try {
      const result = await getAllInvoiceTemplate(opts);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        ConsoleHelper.log(result.data.results);
        setRows(
          result.data.results.map((item) => {
            return { ...item, id: item.template_id };
          }),
        );
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [data.page, data.pageSize, isMounted, searchFilter.ownerId, searchFilter.searchString, sortModel]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  // En nyss skapad mall öppnas direkt, så att man hamnar i texterna i stället för att behöva leta
  // upp raden i listan.
  useEffect(() => {
    if (!templateIdToOpen) return;

    setTemplateId(templateIdToOpen);
    setOpenTemplateDetails(true);

    if (onTemplateOpened) onTemplateOpened();
  }, [templateIdToOpen, onTemplateOpened]);

  const columns: GridColDef[] = [
    {
      field: 'template_id',
      headerName: t('invoice:adminInvoiceTemplatePage.invoiceTemplateNr'),
      sortable: true,
      flex: 0.2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.template_id}</>;
      },
    },
    {
      field: 'name',
      headerName: t('invoice:adminInvoiceTemplatePage.invoiceTemplate'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.name}</>;
      },
    },
    {
      field: 'type',
      headerName: t('invoice:adminInvoiceTemplatePage.type'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        const { type } = params.row;
        return <>{type ? t(`invoice:invoiceMissionTypeText.${type}`) : t('invoice:invoiceMissionTypeText.defaultUnknown')}</>;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
            py: 1,
          },
        }}
      >
        <DataGrid<InvoiceTemplate>
          columns={columns}
          rows={rows}
          autoHeight
          dataModel={data}
          checkboxSelection={false}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            ConsoleHelper.log('newSortModel', newSortModel);

            if (newSortModel.length === 0) {
              const currentSort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
              setSortModel([{ field: sortModel[0].field, sort: currentSort }]);
            } else {
              setSortModel(newSortModel);
            }
          }}
          mode='server'
          onRowClick={onRowClick}
          selectionModel={[]}
          onSelectionModelChange={() => ConsoleHelper.log('')}
        />
      </Box>
      <AdminInvoiceTemplateDetail
        key={templateId}
        openTemplateDetails={openTemplateDetails}
        setOpenTemplateDetails={setOpenTemplateDetails}
        templateId={templateId}
        setTemplateId={setTemplateId}
        updateData={fetchData}
      />
    </>
  );
};
